import { useState, useEffect, useCallback } from "react";
import { Plus, X, Loader2, Trash2, UserCheck, ChevronDown, ChevronUp, Clock, Download, MapPin, Pencil, Save, RotateCcw, CalendarX, CalendarPlus, Check, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAdminData, useAdminCrud } from "@/hooks/useAdminData";
import { formatTimeIT, italianTimeToUtcIso } from "@/utils/datetime";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_LABELS = { start_shift: "Inizio Turno", end_shift: "Fine Turno", start_break: "Inizio Pausa", end_break: "Fine Pausa" };
const EVENT_COLORS = { start_shift: "text-green-600", end_shift: "text-red-500", start_break: "text-amber-500", end_break: "text-blue-500" };
const ABSENCE_TYPES = { ferie: "Ferie", malattia: "Malattia", permesso: "Permesso", infortunio: "Infortunio", altro: "Altro" };
const ABSENCE_COLORS = { ferie: "bg-blue-100 text-blue-700", malattia: "bg-red-100 text-red-600", permesso: "bg-amber-100 text-amber-700", infortunio: "bg-purple-100 text-purple-700", altro: "bg-zinc-100 text-zinc-600" };
const STATUS_LABELS = { approvato: "Approvato", in_attesa: "In Attesa", rifiutato: "Rifiutato" };
const STATUS_COLORS = { approvato: "bg-green-100 text-green-700", in_attesa: "bg-amber-100 text-amber-700", rifiutato: "bg-red-100 text-red-600" };

function TimesheetRow({ ts, driverId, token, onReload }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editEvents, setEditEvents] = useState([]);
  const events = ts.events || [];
  const starts = events.filter((e) => e.type === "start_shift").map((e) => formatTimeIT(e.time));
  const ends = events.filter((e) => e.type === "end_shift").map((e) => formatTimeIT(e.time));
  const nBreaks = events.filter((e) => e.type === "start_break").length;

  const startEdit = () => {
    setEditEvents(events.map((e) => ({ ...e, _time: formatTimeIT(e.time) || "00:00" })));
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      const updatedEvents = editEvents.map((e) => {
        const datePart = ts.date;
        return { type: e.type, time: italianTimeToUtcIso(datePart, e._time), lat: e.lat || null, lng: e.lng || null };
      });
      await axios.put(`${API}/admin/drivers/${driverId}/timesheets/${ts.date}`, { events: updatedEvents }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Ore aggiornate");
      setEditing(false);
      onReload();
    } catch {
      toast.error("Errore salvataggio");
    }
  };

  const deleteDay = async () => {
    if (!window.confirm(`Eliminare la registrazione del ${ts.date.split("-").reverse().join("-")}?`)) return;
    try {
      await axios.delete(`${API}/admin/drivers/${driverId}/timesheets/${ts.date}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Giornata eliminata");
      onReload();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const addEvent = (type) => {
    setEditEvents([...editEvents, { type, _time: "12:00", lat: null, lng: null }]);
  };

  const removeEvent = (idx) => {
    setEditEvents(editEvents.filter((_, i) => i !== idx));
  };

  const updateEventTime = (idx, time) => {
    setEditEvents(editEvents.map((e, i) => (i === idx ? { ...e, _time: time } : e)));
  };

  const googleMapsUrl = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;

  return (
    <>
      <tr
        className="hover:bg-zinc-50 cursor-pointer group"
        onClick={() => setExpanded(!expanded)}
        data-testid={`timesheet-row-${ts.date}`}
      >
        <td className="px-3 py-2 font-bold text-[#09090B]">
          <div className="flex items-center gap-1.5">
            {expanded ? <ChevronUp size={12} className="text-zinc-400" /> : <ChevronDown size={12} className="text-zinc-400" />}
            {ts.date.split("-").reverse().join("-")}
          </div>
        </td>
        <td className="px-3 py-2 text-green-600 font-mono text-xs">{starts.join(", ") || "-"}</td>
        <td className="px-3 py-2 text-red-500 font-mono text-xs">{ends.join(", ") || "-"}</td>
        <td className="px-3 py-2 text-amber-600 font-bold">{nBreaks}</td>
        <td className="px-3 py-2 font-bold text-[#FF5200]">{ts.total_hours?.toFixed(1)}h</td>
        <td className="px-3 py-2 text-zinc-500">{Math.round(ts.total_break_minutes || 0)} min</td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => { e.stopPropagation(); startEdit(); setExpanded(true); }}
              className="p-1 text-zinc-400 hover:text-[#FF5200]"
              title="Modifica"
              data-testid={`edit-timesheet-${ts.date}`}
            >
              <Pencil size={14} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); deleteDay(); }}
              className="p-1 text-zinc-400 hover:text-red-500"
              title="Elimina giornata"
              data-testid={`delete-timesheet-${ts.date}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr data-testid={`timesheet-detail-${ts.date}`}>
          <td colSpan={7} className="px-3 py-2 bg-zinc-50/80">
            {editing ? (
              <div className="space-y-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Modifica eventi — {ts.date.split("-").reverse().join("-")}</p>
                {editEvents.map((ev, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-white border border-zinc-200 rounded px-3 py-1.5">
                    <select
                      value={ev.type}
                      onChange={(e) => setEditEvents(editEvents.map((ee, i) => (i === idx ? { ...ee, type: e.target.value } : ee)))}
                      className="h-7 text-xs border border-zinc-200 rounded-sm px-1"
                      data-testid={`edit-event-type-${idx}`}
                    >
                      <option value="start_shift">Inizio Turno</option>
                      <option value="end_shift">Fine Turno</option>
                      <option value="start_break">Inizio Pausa</option>
                      <option value="end_break">Fine Pausa</option>
                    </select>
                    <input
                      type="time"
                      value={ev._time}
                      onChange={(e) => updateEventTime(idx, e.target.value)}
                      className="h-7 text-xs font-mono border border-zinc-200 rounded-sm px-2"
                      data-testid={`edit-event-time-${idx}`}
                    />
                    {ev.lat && ev.lng && (
                      <a href={googleMapsUrl(ev.lat, ev.lng)} target="_blank" rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-700 text-xs flex items-center gap-0.5" title="Vedi posizione">
                        <MapPin size={12} /> GPS
                      </a>
                    )}
                    <button onClick={() => removeEvent(idx)} className="p-1 text-red-400 hover:text-red-600 ml-auto" data-testid={`remove-event-${idx}`}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={() => addEvent("start_shift")} className="text-[10px] px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 font-bold" data-testid="add-start-shift">+ Inizio Turno</button>
                  <button onClick={() => addEvent("end_shift")} className="text-[10px] px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 font-bold" data-testid="add-end-shift">+ Fine Turno</button>
                  <button onClick={() => addEvent("start_break")} className="text-[10px] px-2 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 font-bold" data-testid="add-start-break">+ Pausa</button>
                  <button onClick={() => addEvent("end_break")} className="text-[10px] px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-bold" data-testid="add-end-break">+ Fine Pausa</button>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-2 border-t border-zinc-200">
                  <button onClick={saveEdit} className="inline-flex items-center gap-1.5 h-8 px-4 bg-[#FF5200] text-white text-xs font-bold uppercase rounded-sm hover:bg-[#e64a00]" data-testid="save-timesheet">
                    <Save size={12} /> Salva
                  </button>
                  <button onClick={() => setEditing(false)} className="inline-flex items-center gap-1.5 h-8 px-4 border border-zinc-200 text-zinc-600 text-xs font-bold uppercase rounded-sm hover:bg-zinc-100" data-testid="cancel-edit-timesheet">
                    <RotateCcw size={12} /> Annulla
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Dettaglio eventi — {ts.date.split("-").reverse().join("-")}</p>
                {events.map((ev, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-xs py-1">
                    <span className="w-4 text-right text-zinc-400 font-mono">{idx + 1}.</span>
                    <span className={`font-bold min-w-[100px] ${EVENT_COLORS[ev.type] || "text-zinc-600"}`}>
                      {EVENT_LABELS[ev.type] || ev.type}
                    </span>
                    <span className="font-mono text-zinc-700">{formatTimeIT(ev.time)}</span>
                    {ev.lat && ev.lng ? (
                      <a
                        href={googleMapsUrl(ev.lat, ev.lng)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-700 hover:underline"
                        data-testid={`gps-link-${ts.date}-${idx}`}
                      >
                        <MapPin size={12} /> {parseFloat(ev.lat).toFixed(4)}, {parseFloat(ev.lng).toFixed(4)}
                      </a>
                    ) : (
                      <span className="text-zinc-300 text-[10px]">— nessun GPS</span>
                    )}
                  </div>
                ))}
                {events.length === 0 && <p className="text-zinc-400 text-xs">Nessun evento registrato</p>}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function ManualTimesheetModal({ driverId, token, defaultDate, onClose, onSaved }) {
  const [date, setDate] = useState(defaultDate);
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("17:00");
  const [breaks, setBreaks] = useState([{ start: "12:00", end: "13:00" }]);
  const [saving, setSaving] = useState(false);

  const addBreak = () => setBreaks([...breaks, { start: "", end: "" }]);
  const removeBreak = (i) => setBreaks(breaks.filter((_, idx) => idx !== i));
  const updateBreak = (i, key, val) => setBreaks(breaks.map((b, idx) => (idx === i ? { ...b, [key]: val } : b)));

  const save = async () => {
    if (!date || !startTime || !endTime) {
      toast.error("Data, inizio e fine turno sono obbligatori");
      return;
    }
    if (endTime <= startTime) {
      toast.error("Fine turno deve essere dopo inizio turno");
      return;
    }
    const events = [
      { type: "start_shift", time: italianTimeToUtcIso(date, startTime), lat: null, lng: null },
    ];
    for (const b of breaks) {
      if (b.start && b.end) {
        if (b.end <= b.start) { toast.error("Fine pausa deve essere dopo inizio pausa"); return; }
        events.push({ type: "start_break", time: italianTimeToUtcIso(date, b.start), lat: null, lng: null });
        events.push({ type: "end_break", time: italianTimeToUtcIso(date, b.end), lat: null, lng: null });
      }
    }
    events.push({ type: "end_shift", time: italianTimeToUtcIso(date, endTime), lat: null, lng: null });
    events.sort((a, b) => a.time.localeCompare(b.time));

    setSaving(true);
    try {
      await axios.put(`${API}/admin/drivers/${driverId}/timesheets/${date}`, { events }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Giornata salvata");
      onSaved();
      onClose();
    } catch {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="manual-timesheet-modal">
      <div className="bg-white rounded-lg w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#09090B]">Aggiungi Giornata Manualmente</h3>
          <button onClick={onClose} className="p-1 text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">Data</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" data-testid="manual-ts-date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-green-600 mb-1">Inizio Turno</label>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm font-mono" data-testid="manual-ts-start" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-red-500 mb-1">Fine Turno</label>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
                className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm font-mono" data-testid="manual-ts-end" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Pause</label>
              <button onClick={addBreak} className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 font-bold" data-testid="manual-ts-add-break">+ Aggiungi Pausa</button>
            </div>
            <div className="space-y-2">
              {breaks.map((b, i) => (
                <div key={i} className="flex items-center gap-2 bg-amber-50/50 border border-amber-100 rounded px-2 py-1.5">
                  <span className="text-[10px] font-bold text-amber-700 w-12">Pausa {i + 1}</span>
                  <input type="time" value={b.start} onChange={(e) => updateBreak(i, "start", e.target.value)}
                    className="h-7 px-2 border border-zinc-200 rounded-sm text-xs font-mono" data-testid={`manual-ts-break-start-${i}`} />
                  <span className="text-zinc-400 text-xs">→</span>
                  <input type="time" value={b.end} onChange={(e) => updateBreak(i, "end", e.target.value)}
                    className="h-7 px-2 border border-zinc-200 rounded-sm text-xs font-mono" data-testid={`manual-ts-break-end-${i}`} />
                  <button onClick={() => removeBreak(i)} className="ml-auto p-1 text-red-400 hover:text-red-600"><X size={12} /></button>
                </div>
              ))}
              {breaks.length === 0 && <p className="text-xs text-zinc-400 italic">Nessuna pausa</p>}
            </div>
          </div>

          <p className="text-[11px] text-zinc-500 italic">Salvando, eventuali eventi gia' presenti per questo giorno verranno sostituiti.</p>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 px-5 py-3">
          <button onClick={onClose} className="h-8 px-4 border border-zinc-200 text-zinc-600 text-xs font-bold uppercase rounded-sm hover:bg-zinc-100">Annulla</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 h-8 px-4 bg-[#FF5200] text-white text-xs font-bold uppercase rounded-sm hover:bg-[#e64a00] disabled:opacity-50" data-testid="manual-ts-save">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Salva
          </button>
        </div>
      </div>
    </div>
  );
}

function DriverTimesheets({ driverId, token }) {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  const loadSheets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/drivers/${driverId}/timesheets?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSheets(res.data);
    } catch {
      toast.error("Errore caricamento ore");
    } finally {
      setLoading(false);
    }
  }, [driverId, month, token]);

  useEffect(() => {
    loadSheets();
  }, [loadSheets]);

  const downloadPdf = () => {
    window.open(`${API}/admin/drivers/${driverId}/timesheets/pdf?month=${month}&token=${token}`, "_blank");
  };

  const totalHours = sheets.reduce((s, t) => s + (t.total_hours || 0), 0);
  const totalBreak = sheets.reduce((s, t) => s + (t.total_break_minutes || 0), 0);

  const monthLabel = (() => {
    const [y, m] = month.split("-");
    const names = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
    return `${names[parseInt(m) - 1]} ${y}`;
  })();

  return (
    <div className="p-4 bg-zinc-50 border-t border-zinc-200" data-testid={`driver-timesheets-${driverId}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Clock size={16} className="text-[#FF5200]" />
          <span className="text-sm font-bold uppercase tracking-wider text-zinc-700">Ore Lavorate</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-8 px-2 border border-zinc-200 rounded-sm text-xs"
            data-testid={`timesheet-month-${driverId}`}
          />
          <button
            onClick={() => setShowManual(true)}
            className="inline-flex items-center gap-1.5 h-8 px-3 bg-[#09090B] text-white text-xs font-bold uppercase rounded-sm hover:bg-zinc-800"
            data-testid={`timesheet-add-manual-${driverId}`}
          >
            <Plus size={12} /> Aggiungi giornata
          </button>
          <button
            onClick={downloadPdf}
            className="inline-flex items-center gap-1.5 h-8 px-3 bg-[#FF5200] text-white text-xs font-bold uppercase rounded-sm hover:bg-[#e64a00]"
            data-testid={`timesheet-pdf-${driverId}`}
          >
            <Download size={12} /> PDF {monthLabel}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="bg-white border border-zinc-200 rounded p-3 text-center">
          <p className="text-xl font-bold text-[#FF5200]">{totalHours.toFixed(1)}h</p>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Ore totali</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded p-3 text-center">
          <p className="text-xl font-bold text-amber-500">{Math.round(totalBreak)} min</p>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Pause totali</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded p-3 text-center">
          <p className="text-xl font-bold text-zinc-700">{sheets.length}</p>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Giorni lavorati</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-6"><Loader2 size={20} className="animate-spin mx-auto text-zinc-400" /></div>
      ) : sheets.length === 0 ? (
        <p className="text-center text-zinc-400 text-sm py-4">Nessuna registrazione per {monthLabel}</p>
      ) : (
        <div className="bg-white border border-zinc-200 overflow-x-auto rounded">
          <table className="w-full text-left text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="px-3 py-2 text-[10px] font-bold tracking-widest uppercase text-zinc-500">Data</th>
                <th className="px-3 py-2 text-[10px] font-bold tracking-widest uppercase text-zinc-500">Inizio</th>
                <th className="px-3 py-2 text-[10px] font-bold tracking-widest uppercase text-zinc-500">Fine</th>
                <th className="px-3 py-2 text-[10px] font-bold tracking-widest uppercase text-zinc-500">Pause</th>
                <th className="px-3 py-2 text-[10px] font-bold tracking-widest uppercase text-zinc-500">Ore</th>
                <th className="px-3 py-2 text-[10px] font-bold tracking-widest uppercase text-zinc-500">Min. Pausa</th>
                <th className="px-3 py-2 text-[10px] font-bold tracking-widest uppercase text-zinc-500 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {sheets.map((ts) => (
                <TimesheetRow key={ts.date} ts={ts} driverId={driverId} token={token} onReload={loadSheets} />
              ))}
              <tr className="bg-[#09090B] text-white font-bold">
                <td className="px-3 py-2" colSpan={4}>TOTALE {monthLabel.toUpperCase()}</td>
                <td className="px-3 py-2 text-[#FF5200]">{totalHours.toFixed(1)}h</td>
                <td className="px-3 py-2" colSpan={2}>{Math.round(totalBreak)} min</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {showManual && (
        <ManualTimesheetModal
          driverId={driverId}
          token={token}
          defaultDate={new Date().toISOString().slice(0, 10)}
          onClose={() => setShowManual(false)}
          onSaved={loadSheets}
        />
      )}
    </div>
  );
}

export default function AdminDrivers() {
  const { data: drivers, reload } = useAdminData("drivers");
  const { create, remove } = useAdminCrud("drivers");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [expandedSection, setExpandedSection] = useState("timesheets");
  const [todayAbsences, setTodayAbsences] = useState([]);
  const [editingDriver, setEditingDriver] = useState(null);
  const [driverEdit, setDriverEdit] = useState({ name: "", surname: "", phone: "", vehicle_plate: "", password: "" });
  const [savingDriver, setSavingDriver] = useState(false);
  const [form, setForm] = useState({ name: "", surname: "", phone: "", vehicle_plate: "", password: "" });
  const token = localStorage.getItem("mv_admin_token");

  const startEditDriver = (d) => {
    setEditingDriver(d.id);
    setDriverEdit({ name: d.name || "", surname: d.surname || "", phone: d.phone || "", vehicle_plate: d.vehicle_plate || "", password: "" });
  };

  const saveDriver = async (driverId) => {
    setSavingDriver(true);
    try {
      await axios.put(`${API}/admin/drivers/${driverId}`, driverEdit, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Autista aggiornato");
      setEditingDriver(null);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setSavingDriver(false);
  };

  useEffect(() => {
    const hdr = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/admin/absences/today`, hdr)
      .then(r => setTodayAbsences(r.data || []))
      .catch(() => {});
  }, [token]);

  const createDriver = async (e) => {
    e.preventDefault();
    if (!form.name || !form.surname || !form.phone || !form.password) { toast.error("Compila tutti i campi obbligatori"); return; }
    setCreating(true);
    try {
      await create(form);
      toast.success("Autista creato con successo!");
      setShowCreate(false);
      setForm({ name: "", surname: "", phone: "", vehicle_plate: "", password: "" });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione");
    } finally {
      setCreating(false);
    }
  };

  const deleteDriver = async (id, name) => {
    if (!window.confirm(`Eliminare l'autista ${name}?`)) return;
    try {
      await remove(id);
      toast.success("Autista eliminato");
      reload();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  return (
    <div data-testid="admin-drivers">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-zinc-500">{drivers.length} autisti registrati</p>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-4 py-2 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors"
          data-testid="create-driver-btn"
        >
          <Plus size={16} /> Nuovo Autista
        </button>
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-left" data-testid="drivers-table">
          <thead className="border-b border-zinc-200 bg-zinc-50">
            <tr>
              <th colSpan={5} className="p-0">
                <div className="grid grid-cols-[2fr_1fr_1fr_1.2fr_auto] px-6 py-3">
                  <span className="text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Nome</span>
                  <span className="text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Telefono</span>
                  <span className="text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Targa</span>
                  <span className="text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Credenziali App</span>
                  <span className="text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading w-20 text-right">Azioni</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {drivers.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-12 text-center">
                <UserCheck size={40} className="text-zinc-300 mx-auto mb-3" />
                <p className="text-zinc-500">Nessun autista registrato</p>
              </td></tr>
            ) : (
              drivers.map((d) => (
                <tr key={d.id} className="group" data-testid={`driver-row-${d.id}`}>
                  <td colSpan={5} className="p-0">
                    <div
                      className="grid grid-cols-[2fr_1fr_1fr_1.2fr_auto] items-center px-6 py-4 cursor-pointer hover:bg-zinc-50"
                      onClick={() => setExpanded(expanded === d.id ? null : d.id)}
                      data-testid={`driver-expand-${d.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-500/10 rounded-full flex items-center justify-center text-purple-600 text-sm font-bold shrink-0">
                          {d.name?.charAt(0)}{d.surname?.charAt(0)}
                        </div>
                        <span className="text-sm font-bold text-[#09090B]">{d.name} {d.surname}</span>
                        {todayAbsences.some(a => a.driver_id === d.id) && (
                          <span className="px-1.5 py-0.5 bg-red-500 text-white text-[9px] font-bold uppercase rounded-sm animate-pulse" data-testid={`absent-badge-${d.id}`}>
                            Assente Oggi
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-zinc-600">{d.phone}</span>
                      <span className="px-2 py-1 bg-zinc-100 text-xs font-mono font-bold text-zinc-700 rounded-sm w-fit">{d.vehicle_plate}</span>
                      <span className="text-xs text-zinc-500">Login: <span className="font-mono">{d.phone}</span></span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); startEditDriver(d); }}
                          className="p-1 text-zinc-400 hover:text-[#FF5200]"
                          data-testid={`edit-driver-${d.id}`}
                          title="Modifica"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteDriver(d.id, `${d.name} ${d.surname}`); }}
                          className="p-1 text-red-400 hover:text-red-600"
                          data-testid={`delete-driver-${d.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                        {expanded === d.id ? <ChevronUp size={16} className="text-zinc-400" /> : <ChevronDown size={16} className="text-zinc-400" />}
                      </div>
                    </div>
                    {/* Edit driver inline form */}
                    {editingDriver === d.id && (
                      <div className="px-6 py-4 bg-amber-50 border-t border-amber-200" onClick={(e) => e.stopPropagation()} data-testid={`edit-form-${d.id}`}>
                        <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Modifica Autista</p>
                        <div className="grid grid-cols-5 gap-3">
                          <div>
                            <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Nome</label>
                            <input value={driverEdit.name} onChange={(e) => setDriverEdit({ ...driverEdit, name: e.target.value })}
                              className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" data-testid="edit-driver-name" />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Cognome</label>
                            <input value={driverEdit.surname} onChange={(e) => setDriverEdit({ ...driverEdit, surname: e.target.value })}
                              className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" data-testid="edit-driver-surname" />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Telefono</label>
                            <input value={driverEdit.phone} onChange={(e) => setDriverEdit({ ...driverEdit, phone: e.target.value })}
                              className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" data-testid="edit-driver-phone" />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Targa</label>
                            <input value={driverEdit.vehicle_plate} onChange={(e) => setDriverEdit({ ...driverEdit, vehicle_plate: e.target.value })}
                              className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm font-mono" data-testid="edit-driver-plate" />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Nuova Password</label>
                            <input type="password" value={driverEdit.password} onChange={(e) => setDriverEdit({ ...driverEdit, password: e.target.value })}
                              placeholder="Lascia vuoto per non cambiare"
                              className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" data-testid="edit-driver-password" />
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button onClick={() => saveDriver(d.id)} disabled={savingDriver} data-testid={`save-driver-${d.id}`}
                            className="inline-flex items-center gap-1.5 px-4 h-9 bg-[#FF5200] text-white text-xs font-bold uppercase rounded-sm hover:bg-[#e64a00] disabled:opacity-50">
                            {savingDriver ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salva
                          </button>
                          <button onClick={() => setEditingDriver(null)} className="px-4 h-9 text-xs text-zinc-500 border border-zinc-200 rounded-sm bg-white">Annulla</button>
                        </div>
                      </div>
                    )}
                    {expanded === d.id && (
                      <div className="border-t border-zinc-200">
                        <div className="flex bg-zinc-50 border-b border-zinc-200">
                          <button onClick={(e) => { e.stopPropagation(); setExpandedSection("timesheets"); }}
                            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${expandedSection === "timesheets" ? "bg-white text-[#FF5200] border-b-2 border-[#FF5200]" : "text-zinc-500"}`}>
                            <Clock size={13} /> Ore Lavorate
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); setExpandedSection("absences"); }}
                            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${expandedSection === "absences" ? "bg-white text-[#FF5200] border-b-2 border-[#FF5200]" : "text-zinc-500"}`}>
                            <CalendarX size={13} /> Assenze
                          </button>
                        </div>
                        {expandedSection === "timesheets" && <DriverTimesheets driverId={d.id} token={token} />}
                        {expandedSection === "absences" && <DriverAbsences driverId={d.id} token={token} />}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-driver-modal">
          <div className="bg-white w-full max-w-md">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold uppercase">Nuovo Autista</h3>
              <button onClick={() => setShowCreate(false)} className="p-2 text-zinc-400 hover:text-zinc-600"><X size={20} /></button>
            </div>
            <form onSubmit={createDriver} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="driver-name" className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Nome *</label>
                  <input id="driver-name" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" data-testid="driver-name" />
                </div>
                <div>
                  <label htmlFor="driver-surname" className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Cognome *</label>
                  <input id="driver-surname" type="text" value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })}
                    className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" data-testid="driver-surname" />
                </div>
              </div>
              <div>
                <label htmlFor="driver-phone" className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Telefono * (login)</label>
                <input id="driver-phone" type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" placeholder="3331234567" data-testid="driver-phone" />
              </div>
              <div>
                <label htmlFor="driver-plate" className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Targa Veicolo</label>
                <input id="driver-plate" type="text" value={form.vehicle_plate} onChange={(e) => setForm({ ...form, vehicle_plate: e.target.value.toUpperCase() })}
                  className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none font-mono uppercase" placeholder="AB123CD" data-testid="driver-plate" />
              </div>
              <div>
                <label htmlFor="driver-password" className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Password * (app)</label>
                <input id="driver-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" data-testid="driver-password" />
              </div>
              <button type="submit" disabled={creating}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#FF5200] text-white h-10 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors text-sm disabled:opacity-50"
                data-testid="driver-create-submit">
                {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Crea Autista
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}


function DriverAbsences({ driverId, token }) {
  const [data, setData] = useState({ absences: [], total_days: 0, by_type: {} });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [absForm, setAbsForm] = useState({ date_from: "", date_to: "", absence_type: "ferie", partial: false, time_from: "", time_to: "", note: "", status: "approvato" });
  const [saving, setSaving] = useState(false);
  const [absMonth, setAbsMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const h = { headers: { Authorization: `Bearer ${token}` } };

  const loadAbsences = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/drivers/${driverId}/absences`, h);
      setData(r.data);
    } catch { toast.error("Errore caricamento assenze"); }
    setLoading(false);
  }, [driverId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadAbsences(); }, [loadAbsences]);

  const addAbsence = async (e) => {
    e.preventDefault();
    if (!absForm.date_from || !absForm.date_to) return toast.error("Inserisci date inizio e fine");
    setSaving(true);
    try {
      await axios.post(`${API}/admin/absences`, { driver_id: driverId, ...absForm }, h);
      toast.success("Assenza registrata");
      setShowAdd(false);
      setAbsForm({ date_from: "", date_to: "", absence_type: "ferie", partial: false, time_from: "", time_to: "", note: "", status: "approvato" });
      loadAbsences();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/admin/absences/${id}`, { status }, h);
    toast.success(status === "approvato" ? "Approvata" : "Rifiutata");
    loadAbsences();
  };

  const deleteAbsence = async (id) => {
    if (!window.confirm("Eliminare questa assenza?")) return;
    await axios.delete(`${API}/admin/absences/${id}`, h);
    toast.success("Assenza eliminata");
    loadAbsences();
  };

  const formatDate = (d) => d ? d.split("-").reverse().join("/") : "-";

  // Filter absences by selected month
  const monthEnd = `${absMonth}-31`;
  const monthStart = `${absMonth}-01`;
  const filteredAbsences = data.absences.filter(a => a.date_from <= monthEnd && a.date_to >= monthStart);
  let filteredDays = 0;
  let filteredHours = 0;
  const filteredByType = {};
  filteredAbsences.forEach(a => {
    if (a.status !== "approvato") return;
    const t = a.absence_type || "altro";
    if (a.partial && a.time_from && a.time_to) {
      try { const [h1,m1]=a.time_from.split(":").map(Number); const [h2,m2]=a.time_to.split(":").map(Number); filteredHours += (h2*60+m2-h1*60-m1)/60; } catch { /* malformed time, skip */ }
    } else {
      try { const d1=new Date(a.date_from); const d2=new Date(a.date_to); const days=Math.round((d2-d1)/86400000)+1; filteredDays+=days; filteredByType[t]=(filteredByType[t]||0)+days; } catch { /* malformed date, skip */ }
    }
  });
  const totalHoursCalc = filteredDays * 8 + filteredHours;

  return (
    <div className="p-4 bg-zinc-50" data-testid={`driver-absences-${driverId}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <CalendarX size={16} className="text-red-500" />
          <span className="text-sm font-bold uppercase tracking-wider text-zinc-700">Assenze</span>
          <input type="month" value={absMonth} onChange={(e) => setAbsMonth(e.target.value)}
            className="h-8 px-2 border border-zinc-200 rounded-sm text-sm bg-white" data-testid={`abs-month-${driverId}`} />
          <span className="text-xs text-zinc-400">({filteredDays}gg{filteredHours > 0 ? ` + ${filteredHours.toFixed(0)}h` : ""} = {totalHoursCalc.toFixed(0)}h)</span>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} data-testid={`add-absence-${driverId}`}
          className="inline-flex items-center gap-1.5 h-8 px-3 bg-[#FF5200] text-white text-xs font-bold uppercase rounded-sm hover:bg-[#e64a00]">
          <CalendarPlus size={12} /> Aggiungi
        </button>
      </div>

      {/* Summary by type */}
      {Object.keys(filteredByType).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {Object.entries(filteredByType).map(([type, days]) => (
            <span key={type} className={`px-2 py-1 text-xs font-bold rounded-sm ${ABSENCE_COLORS[type] || "bg-zinc-100 text-zinc-600"}`}>
              {ABSENCE_TYPES[type] || type}: {days}g
            </span>
          ))}
        </div>
      )}

      {/* Add form */}
      {showAdd && (
        <form onSubmit={addAbsence} className="bg-white border border-zinc-200 rounded-sm p-4 mb-3 space-y-3" data-testid="absence-form">
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Dal *</label>
              <input type="date" value={absForm.date_from} onChange={e => setAbsForm({ ...absForm, date_from: e.target.value })}
                className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" data-testid="abs-date-from" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Al *</label>
              <input type="date" value={absForm.date_to} onChange={e => setAbsForm({ ...absForm, date_to: e.target.value })}
                className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" data-testid="abs-date-to" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Tipo</label>
              <select value={absForm.absence_type} onChange={e => setAbsForm({ ...absForm, absence_type: e.target.value })}
                className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm" data-testid="abs-type">
                {Object.entries(ABSENCE_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Stato</label>
              <select value={absForm.status} onChange={e => setAbsForm({ ...absForm, status: e.target.value })}
                className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm">
                <option value="approvato">Approvato</option>
                <option value="in_attesa">In Attesa</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="abs-partial-toggle">
              <div className={`relative w-9 h-5 rounded-full transition-colors ${absForm.partial ? "bg-[#FF5200]" : "bg-zinc-300"}`}
                onClick={() => setAbsForm({ ...absForm, partial: !absForm.partial, date_to: absForm.partial ? absForm.date_to : absForm.date_from })}>
                <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${absForm.partial ? "translate-x-4" : ""}`} />
              </div>
              <span className="text-xs font-bold text-zinc-600">Solo alcune ore</span>
            </label>
            {absForm.partial && (
              <>
                <input type="time" value={absForm.time_from} onChange={e => setAbsForm({ ...absForm, time_from: e.target.value })}
                  className="h-9 px-2 border border-zinc-200 rounded-sm text-sm w-28" placeholder="Dalle" data-testid="abs-time-from" />
                <span className="text-zinc-400 text-xs">—</span>
                <input type="time" value={absForm.time_to} onChange={e => setAbsForm({ ...absForm, time_to: e.target.value })}
                  className="h-9 px-2 border border-zinc-200 rounded-sm text-sm w-28" placeholder="Alle" data-testid="abs-time-to" />
              </>
            )}
          </div>
          <input value={absForm.note} onChange={e => setAbsForm({ ...absForm, note: e.target.value })} placeholder="Note (opzionale)"
            className="w-full h-9 px-3 border border-zinc-200 rounded-sm text-sm" />
          <div className="flex gap-2">
            <button type="submit" disabled={saving} data-testid="abs-save-btn"
              className="px-4 h-9 bg-[#FF5200] text-white text-xs font-bold uppercase rounded-sm hover:bg-[#e64a00] disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : "Salva"}
            </button>
            <button type="button" onClick={() => setShowAdd(false)} className="px-4 h-9 text-xs text-zinc-500 border border-zinc-200 rounded-sm">Annulla</button>
          </div>
        </form>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-4"><Loader2 size={20} className="animate-spin mx-auto text-zinc-400" /></div>
      ) : filteredAbsences.length === 0 ? (
        <p className="text-center text-zinc-400 text-sm py-4">Nessuna assenza nel mese selezionato</p>
      ) : (
        <div className="space-y-2">
          {filteredAbsences.map((a) => (
            <div key={a.id} className="bg-white border border-zinc-200 rounded-sm px-4 py-3 flex items-center gap-3" data-testid={`absence-row-${a.id}`}>
              <div className={`px-2 py-1 text-[10px] font-bold uppercase rounded-sm ${ABSENCE_COLORS[a.absence_type] || "bg-zinc-100"}`}>
                {ABSENCE_TYPES[a.absence_type] || a.absence_type}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">{formatDate(a.date_from)} — {formatDate(a.date_to)}{a.partial && a.time_from && a.time_to ? ` (${a.time_from}-${a.time_to})` : ""}</p>
                {a.note && <p className="text-xs text-zinc-500 truncate">{a.note}</p>}
                {a.requested_by === "driver" && <span className="text-[9px] text-violet-500 font-bold uppercase">Richiesta dall'autista</span>}
              </div>
              <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-sm ${STATUS_COLORS[a.status] || "bg-zinc-100"}`}>
                {STATUS_LABELS[a.status] || a.status}
              </span>
              {a.status === "in_attesa" && (
                <div className="flex gap-1">
                  <button onClick={() => updateStatus(a.id, "approvato")} className="p-1.5 bg-green-500 text-white rounded-sm hover:bg-green-600" data-testid={`approve-abs-${a.id}`}><Check size={12} /></button>
                  <button onClick={() => updateStatus(a.id, "rifiutato")} className="p-1.5 bg-red-500 text-white rounded-sm hover:bg-red-600" data-testid={`reject-abs-${a.id}`}><XCircle size={12} /></button>
                </div>
              )}
              <button onClick={() => deleteAbsence(a.id)} className="p-1 text-zinc-300 hover:text-red-500"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
